<template>
    <div class="custom-reference-house-container">
        <h2 class="add-house-modal-heading">Bostadshuset</h2>
        <div class="row corona-options" v-if="isCorona">
            <div class="col-sm-5 col-sm-offset-1">
                <h3>Hustyp</h3>
                <div class="btn-group">
                    <div class="radio">
                        <input type="radio" class="form-control" id="lowRise" v-model="houseConstructionType" value="lowRise"/>
                        <label class="control-label" for="lowRise">Lamellhus</label>
                    </div>
                    <br/>
                    <div class="radio">
                        <input type="radio" class="form-control" id="towerBlock" v-model="houseConstructionType" value="towerBlock"/>
                        <label class="control-label" for="towerBlock">Punkthus</label>
                    </div>
                </div>
            </div>
            <div class="col-sm-5 col-sm-offset-1" v-if="isLowRiseHouse">
                <h3>Loftgång</h3>
                <div class="btn-group">
                    <div class="radio">
                        <input type="radio" class="form-control" id="exteriorCorridorFalse" v-model="hasExteriorCorridor" v-bind:value="false"/>
                        <label class="control-label" for="exteriorCorridorFalse">Nej</label>
                    </div>
                    <br>
                    <div class="radio">
                        <input type="radio" class="form-control" id="exteriorCorridorTrue" v-model="hasExteriorCorridor" v-bind:value="true"/>
                        <label class="control-label" for="exteriorCorridorTrue">Ja</label>
                    </div>
                </div>
            </div>
        </div>
        <div class="row custom-reference-house">
            <div class="col-sm-10 col-sm-offset-1 custom-reference-house-inner" v-bind:class="{ corona: isCorona }">
                <h3>Normalplan</h3>
                <table class="table">
                    <thead>
                    <tr>
                        <th>Storlek</th>
                        <th>Yta</th>
                        <th>Antal</th>
                    </tr>
                    </thead>
                    <tbody>
                    <custom-reference-house-apartment
                            v-for="(apartment, i) in standardFloorApartments"
                            :apartment="apartment"
                            :key="apartment.id"
                            :showAddButton="i === 0"
                            :showRemoveButton="i !== 0"
                            :handleAdd="() => addApartment(standardFloorApartments)"
                            :handleRemove="(apartmentId) => removeApartment(standardFloorApartments, apartmentId)"/>
                    </tbody>
                </table>

                <p class="groundfloor-selector-text" v-if="!isGroundFloorSelectorVisible">
                    Om du vill fylla i ett annorlunda markplan från normalplan,
                    <a href="#" @click.prevent="showGroundFloorSelector()"
                       class="reference-house-filter-toggle">klicka här</a>
                </p>

                <p class="groundfloor-selector-text" v-if="isGroundFloorSelectorVisible">
                    Om markplanet inte skiljer sig från normalplanen,
                    <a href="#" @click.prevent="hideGroundFloorSelector()"
                       class="reference-house-filter-toggle">klicka här</a>
                </p>

                <transition name="fade">
                    <div v-if="isGroundFloorSelectorVisible" class="groundfloor-inputs">
                        <h3>Markplan</h3>
                        <table class="table">
                            <thead>
                            <tr>
                                <th>Storlek</th>
                                <th>Yta</th>
                                <th>Antal</th>
                            </tr>
                            </thead>
                            <tbody>
                            <custom-reference-house-apartment
                                    v-for="(apartment, i) in groundFloorApartments"
                                    :apartment="apartment"
                                    :key="apartment.id"
                                    :showAddButton="i === 0"
                                    :showRemoveButton="i !== 0"
                                    :handleAdd="() => addApartment(groundFloorApartments)"
                                    :handleRemove="(apartmentId) => removeApartment(groundFloorApartments, apartmentId)"/>
                            </tbody>
                        </table>
                    </div>
                </transition>
            </div>

            <div class="row top-buffer modal-actions">
                <div class="col-sm-6 text-left">
                    <a href="#" @click.prevent="onCancel()">Avbryt</a>
                </div>
                <div class="col-sm-6 text-right">
                    <button type="submit" class="btn btn-primary"
                            @click.prevent="onOk({
                            groundFloorApartments,
                            standardFloorApartments,
                            houseConstructionType,
                            hasExteriorCorridor: canHaveExteriorCorridor && hasExteriorCorridor
                            })">
                        <span class="spinner">Använd detta hus</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState} from "vuex"
    import _ from "lodash"
    import CustomReferenceHouseApartment from "./customRefHouseApartment"

    const defaultApartment = {
        id: 0,
        size: 3,
        area: 0.0,
        count: 0
    }

    export default {
        data() {
            return {
                standardFloorApartments: [],
                groundFloorApartments: [],
                isGroundFloorSelectorVisible: false,
                houseConstructionType: "lowRise",
                hasExteriorCorridor: false
            }
        },

        props: {
            onCancel: {type: Function},
            onOk: {type: Function},
        },

        computed: {
            ...mapState({
                currentCustomReferenceHouse: state => state.refHouses.currentCustomReferenceHouse,
                isCorona: state => state.procurementModel === 4,
                lastHouse: state => _.last(state.houses.houses)
            }),
            isLowRiseHouse() {
                return this.houseConstructionType === 'lowRise';
            },
            canHaveExteriorCorridor() {
                return this.isLowRiseHouse;
            }
        },

        methods: {
            addApartment(apartments) {
                apartments.push({...defaultApartment, id: _.last(apartments).id + 1})
            },
            removeApartment(apartments, apartment) {
                const index = apartments.indexOf(apartment)
                apartments.splice(index, 1)
            },
            showGroundFloorSelector() {
                this.isGroundFloorSelectorVisible = true;
            },
            hideGroundFloorSelector() {
                this.isGroundFloorSelectorVisible = false;
                this.groundFloorApartments = [];
                this.initGroundFloorApartments();
            },
            initGroundFloorApartments() {
                if (_.isEmpty(this.currentCustomReferenceHouse.groundFloorApartments)) {
                    this.groundFloorApartments.push(_.clone(defaultApartment))
                } else {
                    if(this.lastHouse) {
                        this.houseConstructionType = this.lastHouse.houseConstructionType;
                        this.hasExteriorCorridor = this.lastHouse.hasExteriorCorridor;
                    }
                    this.currentCustomReferenceHouse.groundFloorApartments.forEach(apartment => {
                        this.groundFloorApartments.push(_.clone(apartment))
                    })
                }
            }
        },

        components: {
            CustomReferenceHouseApartment,
        },

        mounted() {
            if (_.isEmpty(this.currentCustomReferenceHouse.standardFloorApartments)) {
                this.standardFloorApartments.push(_.clone(defaultApartment))
            } else {
                this.currentCustomReferenceHouse.standardFloorApartments.forEach(apartment => {
                    this.standardFloorApartments.push(_.clone(apartment))
                })
            }

            this.initGroundFloorApartments();
        },
    }
</script>

<style lang="scss">
    @import './assets/stylesheets/components/radiobuttons';
    @import './assets/stylesheets/base/colors';
    @import './assets/stylesheets/base/typography';

    .custom-reference-house-container {

        .custom-reference-house-inner {
            min-height: 640px;

            &.corona {
                min-height: 380px;
            }

            .groundfloor-selector-text {
                margin: 20px 0;
            }
        }

        table {
            position: relative;
            width: 92%;
            margin-left: 4%;
            margin-bottom: 50px;

            > thead > tr > th {
                @extend .futura-book;
                @include font-size(16);
                padding-bottom: 16px;
                border: none;
                text-align: center;
            }

            select {
                position: absolute;
                top: 15px;
            }

            > tbody > tr > td {
                border: 1px solid $black;
                position: relative;

                &:first-of-type {
                    min-width: 150px;
                }
            }

            .row-button {
                @include font-size(20);
                position: absolute;
                top: 15px;
                color: $sabo-blue;
                cursor: pointer;

                &:hover {
                    color: $default-button-color-focus;
                }

                &:hover {
                    color: $default-button-color-active;
                }
            }

            .add-row-button {
                @extend .row-button;
                left: -26px;
            }

            .remove-row-button {
                @extend .row-button;
                left: 103%;
                color: $remove-row--default;

                &:hover {
                    color: $remove-row--hover;
                }

                &:active {
                    color: $remove-row--active;
                }
            }

            .added .add-row-button,
            .original .remove-row-button {
                display: none;
            }
        }

        .corona-options {
            margin-top: 2rem;
            > div {
                margin-bottom: 2rem;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// assets/vue/houseModel/components/customRefHouse.vue